import React, { useState, useEffect } from 'react';
import { CreditCard, Download, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { formatCents } from '../../utils/credits';

interface PaymentMethodsResponse {
  has_payment_method: boolean;
  default_card: {
    last4: string;
    brand: string;
  } | null;
}

interface BillingInfoResponse {
  company_name: string | null;
  tax_id: string | null;
  billing_address: string | null;
}

interface Invoice {
  id: string;
  invoice_type: 'topup' | 'subscription';
  invoice_date: string;
  amount: number;
  status: string;
  description: string;
  payment_reference?: string;
  citypay_transaction_id?: string;
  topup_type?: string;
  credits_granted?: number;
  subscription_id?: string;
  period_start?: string;
  period_end?: string;
}

const BillingSection = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Billing info state
  const [billingInfo, setBillingInfo] = useState<BillingInfoResponse>({
    company_name: null,
    tax_id: null,
    billing_address: null,
  });
  const [savingBillingInfo, setSavingBillingInfo] = useState(false);
  const [billingInfoSuccess, setBillingInfoSuccess] = useState(false);
  
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  
  const api = useAuthenticatedApi();

  useEffect(() => {
    if (api) {
      fetchPaymentMethods();
      fetchBillingInfo();
      fetchInvoices();
    }
  }, [api]);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<PaymentMethodsResponse>(apiEndpoints.billing.paymentMethods);
      setPaymentMethods(response);
    } catch (err: any) {
      console.error('Error fetching payment methods:', err);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      const response = await api.get<BillingInfoResponse>(apiEndpoints.billing.billingInfo);
      setBillingInfo(response);
    } catch (err: any) {
      console.error('Error fetching billing info:', err);
    }
  };

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await api.get<Invoice[]>(apiEndpoints.billing.invoices);
      setInvoices(response);
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleBillingInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingBillingInfo(true);
    setBillingInfoSuccess(false);
    
    try {
      await api.put(apiEndpoints.billing.billingInfo, billingInfo);
      setBillingInfoSuccess(true);
      setTimeout(() => setBillingInfoSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving billing info:', err);
      setError('Failed to save billing information');
    } finally {
      setSavingBillingInfo(false);
    }
  };

  const handleBillingInfoChange = (field: keyof BillingInfoResponse, value: string) => {
    setBillingInfo(prev => ({
      ...prev,
      [field]: value || null
    }));
  };

  const getInvoiceStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      processing: { color: 'bg-blue-100 text-blue-800', label: 'Processing' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Failed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium ${config.color} rounded-full`}>
        {config.label}
      </span>
    );
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleDownloadInvoice = async (invoiceId: string, invoiceType: string) => {
    try {
      // Create the download URL with query parameter
      const downloadUrl = `${apiEndpoints.billing.invoices}/${invoiceId}/download?invoice_type=${invoiceType}`;
      
      // Get the auth token
      const token = await api.getToken();
      
      // Use fetch directly for blob response
      const response = await fetch(`${api.getBaseUrl()}${downloadUrl}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Generate filename with timestamp (matching backend format: YYYYMMDD_HHMMSS in UTC)
      const now = new Date();
      const year = now.getUTCFullYear();
      const month = String(now.getUTCMonth() + 1).padStart(2, '0');
      const day = String(now.getUTCDate()).padStart(2, '0');
      const hours = String(now.getUTCHours()).padStart(2, '0');
      const minutes = String(now.getUTCMinutes()).padStart(2, '0');
      const seconds = String(now.getUTCSeconds()).padStart(2, '0');
      const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
      const filename = `invoice_${invoiceId}_${timestamp}.pdf`;
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setError('Failed to download invoice. Please try again.');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">Billing & Payments</h2>
        <p className="text-neutral-600 dark:text-neutral-400">Manage your billing information and payment methods.</p>
      </div>

      <div className="space-y-8">
        {/* Payment Methods */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Payment Methods</h3>
          </div>
          
          <div className="space-y-3">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
              </div>
            )}
            {error && (
              <div className="flex items-center justify-center py-8 text-red-500">
                <AlertCircle className="h-6 w-6 mr-2" />
                {error}
              </div>
            )}
            {!loading && !error && paymentMethods && (
              paymentMethods.has_payment_method ? (
                <div className="flex items-center justify-between p-4 bg-ui-background-secondary rounded-lg border border-ui-border-primary">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                      <CreditCard size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ui-text-primary">
                          **** **** **** {paymentMethods.default_card?.last4}
                        </span>
                        {paymentMethods.default_card && (
                          <span className="px-2 py-1 text-xs bg-brand-primary text-black rounded">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-ui-text-secondary">{paymentMethods.default_card?.brand || 'Card'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-ui-text-secondary mb-4">No payment methods added yet.</p>
                  <p className="text-sm text-ui-text-secondary">Add a payment method to enable one-click purchases and subscriptions.</p>
                </div>
              )
            )}
          </div>
        </div>

        {/* Billing Information */}
        <div>
          <h3 className="text-lg font-semibold text-ui-text-primary mb-4">Billing Information</h3>
          <div className="p-4 bg-ui-background-secondary rounded-lg border border-ui-border-primary">
            <form onSubmit={handleBillingInfoSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ui-text-primary mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-ui-border-primary rounded-lg bg-ui-background-primary text-ui-text-primary"
                    placeholder="Your Company"
                    value={billingInfo.company_name || ''}
                    onChange={(e) => handleBillingInfoChange('company_name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ui-text-primary mb-2">
                    Tax ID
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-ui-border-primary rounded-lg bg-ui-background-primary text-ui-text-primary"
                    placeholder="Tax ID"
                    value={billingInfo.tax_id || ''}
                    onChange={(e) => handleBillingInfoChange('tax_id', e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-ui-text-primary mb-2">
                    Billing Address
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-2 border border-ui-border-primary rounded-lg bg-ui-background-primary text-ui-text-primary"
                    placeholder="Enter billing address"
                    value={billingInfo.billing_address || ''}
                    onChange={(e) => handleBillingInfoChange('billing_address', e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-ui-background-primary text-ui-text-primary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors disabled:opacity-50 flex items-center"
                  disabled={savingBillingInfo}
                >
                  {savingBillingInfo ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Update Billing Info'
                  )}
                </button>
                {billingInfoSuccess && (
                  <span className="text-green-500 text-sm flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Billing information updated!
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Invoice History */}
        <div>
          <h3 className="text-lg font-semibold text-ui-text-primary mb-4">Invoice History</h3>
          <div className="bg-ui-background-secondary rounded-lg border border-ui-border-primary overflow-hidden">
            {invoicesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-ui-text-secondary">No invoices found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-ui-background-tertiary">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ui-text-secondary uppercase">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ui-text-secondary uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ui-text-secondary uppercase">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ui-text-secondary uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ui-text-secondary uppercase">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ui-border-primary">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-ui-background-primary">
                        <td className="px-6 py-4 text-sm text-ui-text-primary">
                          <div>
                            <div className="font-medium">{invoice.description}</div>
                            {invoice.credits_granted && (
                              <div className="text-xs text-ui-text-secondary mt-1">
                                Credits: {formatCents(invoice.credits_granted ?? 0)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-ui-text-secondary">
                          {new Date(invoice.invoice_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-ui-text-primary">
                          {formatAmount(invoice.amount)}
                        </td>
                        <td className="px-6 py-4">
                          {getInvoiceStatusBadge(invoice.status)}
                        </td>
                        <td className="px-6 py-4">
                          <button 
                            onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_type)}
                            className="flex items-center gap-1 text-sm text-brand-primary hover:text-brand-primaryDark"
                          >
                            <Download size={14} />
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSection; 